import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { Google as GoogleIcon, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getGoogleCalendarAuthUrl,
  getGoogleCalendarStatus,
  disconnectGoogleCalendar,
} from '../../services/api/google-calendar/google-calendar';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

export const GoogleCalendarConnection: React.FC = () => {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);

  // Query per verificare lo stato della connessione
  const {
    data: calendarStatus,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['googleCalendarStatus'],
    queryFn: getGoogleCalendarStatus,
    retry: false,
  });

  // Mutation per disconnettere
  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogleCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['googleCalendarStatus'] });
      toast.success('Google Calendar disconnesso con successo');
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Errore durante la disconnessione');
    },
  });

  // Gestione OAuth con popup
  const handleConnectCalendar = async () => {
    try {
      setIsConnecting(true);

      // Ottieni l'URL di autorizzazione
      const { authUrl } = await getGoogleCalendarAuthUrl();

      // Calcola la posizione centrale per il popup
      const left = window.screen.width / 2 - POPUP_WIDTH / 2;
      const top = window.screen.height / 2 - POPUP_HEIGHT / 2;

      // Apri il popup
      const popup = window.open(
        authUrl,
        'GoogleCalendarAuth',
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
      );

      if (!popup) {
        toast.error('Popup bloccato. Abilita i popup per questo sito.');
        setIsConnecting(false);
        return;
      }

      // Monitora la chiusura del popup
      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
          setIsConnecting(false);
          // Ricarica lo stato della connessione
          queryClient.invalidateQueries({ queryKey: ['googleCalendarStatus'] });
        }
      }, 500);

      // Ascolta messaggi dal popup (opzionale, per gestire il callback)
      const messageHandler = (event: MessageEvent) => {
        // Verifica che il messaggio provenga dal dominio corretto
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
          popup.close();
          toast.success('Google Calendar connesso con successo!');
          queryClient.invalidateQueries({ queryKey: ['googleCalendarStatus'] });
          setIsConnecting(false);
          window.removeEventListener('message', messageHandler);
        } else if (event.data?.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
          popup.close();
          toast.error('Errore durante la connessione a Google Calendar');
          setIsConnecting(false);
          window.removeEventListener('message', messageHandler);
        }
      };

      window.addEventListener('message', messageHandler);

      // Cleanup dopo 5 minuti (timeout)
      setTimeout(() => {
        if (popup && !popup.closed) {
          popup.close();
          setIsConnecting(false);
        }
        window.removeEventListener('message', messageHandler);
      }, 5 * 60 * 1000);
    } catch (error: unknown) {
      toast.error(
        (error instanceof Error && error.message) ||
          'Errore durante la connessione'
      );
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    if (
      window.confirm('Sei sicuro di voler disconnettere Google Calendar?')
    ) {
      disconnectMutation.mutate();
    }
  };

  const formatExpiryDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={2}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <GoogleIcon sx={{ mr: 1, color: '#4285F4' }} />
          <Typography variant="h6">Google Calendar</Typography>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Impossibile verificare lo stato della connessione
          </Alert>
        )}

        {calendarStatus?.connected ? (
          <Box>
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                Google Calendar è connesso
                {calendarStatus.expiryDate && (
                  <>
                    <br />
                    Scadenza: {formatExpiryDate(calendarStatus.expiryDate)}
                  </>
                )}
              </Typography>
            </Alert>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
            >
              {disconnectMutation.isPending ? 'Disconnessione...' : 'Disconnetti'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Connetti il tuo account Google Calendar per sincronizzare
              automaticamente gli appuntamenti.
            </Typography>
            <Button
              variant="contained"
              startIcon={isConnecting ? <CircularProgress size={20} /> : <GoogleIcon />}
              onClick={handleConnectCalendar}
              disabled={isConnecting}
              sx={{
                backgroundColor: '#4285F4',
                '&:hover': {
                  backgroundColor: '#357AE8',
                },
              }}
            >
              {isConnecting ? 'Connessione in corso...' : 'Connetti Google Calendar'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};